import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { isDealerUser } from "../lib/roles";
import SoldMarqueeToggle from "../components/SoldMarqueeToggle";

const STATUSES = ["", "new", "working", "appointment", "sold", "lost", "bad"];
const TEMPERATURES = ["", "Hot", "Warm", "Cold"];
const DATE_RANGES = [
  { v: "", label: "Any time" },
  { v: "today", label: "Today" },
  { v: "week", label: "Past 7 days" },
  { v: "month", label: "Past 30 days" },
  { v: "year", label: "Past year" },
];
const MISSING = [
  { v: "", label: "Any" },
  { v: "license", label: "Missing License" },
  { v: "insurance", label: "Missing Insurance" },
  { v: "payoff", label: "Missing Payoff" },
];

export default function AdminCrmSearch() {
  const { user, ready } = useAuth();
  const navigate = useNavigate();
  const [filters, setFilters] = useState({
    q: "", status: "", temperature: "", date_range: "",
    has_trade: "", missing: "", vehicle_make: "", vehicle_model: "",
    vin: "", stock: "",
  });
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  const [advancedOpen, setAdvancedOpen] = useState(false);

  useEffect(() => {
    if (ready && !isDealerUser(user)) navigate("/dealer-login");
  }, [ready, user, navigate]);

  useEffect(() => {
    if (!isDealerUser(user)) return;
    api.get("/admin/crm/dashboard").then(r => setDashboard(r.data.summary)).catch(() => {});
  }, [user]);

  // Hydrate ?q= from the shell global search
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    const initial = {};
    ["q","status","temperature","date_range","has_trade","missing","vehicle_make","vehicle_model","vin","stock"].forEach(k => {
      const v = sp.get(k);
      if (v !== null) initial[k] = v;
    });
    if (Object.keys(initial).length) setFilters(f => ({ ...f, ...initial }));
  }, []);

  const doSearch = async (override = null) => {
    setLoading(true);
    try {
      const src = override || filters;
      const params = {};
      Object.entries(src).forEach(([k, v]) => {
        if (v !== "" && v !== null && v !== undefined) {
          params[k] = k === "has_trade" ? v === "yes" : v;
        }
      });
      const { data } = await api.get("/admin/crm/search", { params });
      setItems(data.items || []);
      setCount(data.count || 0);
    } finally { setLoading(false); }
  };

  useEffect(() => { if (isDealerUser(user)) doSearch(); /* initial */ /* eslint-disable-next-line */ }, [user]);

  const onSubmit = (e) => { e.preventDefault(); doSearch(); };

  const clearFilters = () => {
    const cleared = { q: "", status: "", temperature: "", date_range: "",
      has_trade: "", missing: "", vehicle_make: "", vehicle_model: "",
      vin: "", stock: "" };
    setFilters(cleared);
    doSearch(cleared);
  };

  const update = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  const inputCls = "w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500";
  const labelCls = "text-[11px] font-semibold text-gray-500 uppercase tracking-wider";

  return (
    <div className="bg-slate-50 min-h-full" data-testid="admin-crm-search">
      {/* Lovable command-center header */}
      <div className="bg-gradient-to-b from-[#061426] to-[#0a1f3a] text-white border-b border-white/10">
        <div className="px-5 py-5 flex flex-wrap items-end justify-between gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-blue-300 font-bold">Records</div>
            <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight mt-0.5">Customers</h1>
            <p className="text-sm text-blue-200/70 mt-1">Search every customer across the whole pipeline. PII masked in this list.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Link to="/admin/documents/templates" className="bg-white/10 hover:bg-white/20 border border-white/20 text-white text-xs font-bold px-3 py-2 rounded" data-testid="admin-crm-templates-link">Document Templates</Link>
            <Link to="/admin/crm/misc" className="bg-white/10 hover:bg-white/20 border border-white/20 text-white text-xs font-bold px-3 py-2 rounded" data-testid="admin-crm-misc-link">Misc Documents</Link>
            <Link to="/dealer" className="bg-white/10 hover:bg-white/20 border border-white/20 text-white text-xs font-bold px-3 py-2 rounded" data-testid="admin-crm-back-portal">Dealer Portal</Link>
          </div>
        </div>
      </div>

      <div className="p-5">
        {/* KPI strip */}
        {dashboard && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3 mb-5" data-testid="admin-crm-kpis">
            <Kpi label="Overdue Tasks" value={dashboard.tasks_overdue} accent={dashboard.tasks_overdue ? "red" : null} />
            <Kpi label="Tasks Today" value={dashboard.tasks_today} accent="blue" />
            <Kpi label="Upcoming Appts" value={dashboard.appts_upcoming} accent="blue" />
            <Kpi label="Hot Leads" value={dashboard.hot_leads} accent="amber" />
            <Kpi label="Missing License" value={dashboard.missing_license} accent={dashboard.missing_license ? "amber" : null} />
            <Kpi label="Missing Insurance" value={dashboard.missing_insurance} accent={dashboard.missing_insurance ? "amber" : null} />
            <Kpi label="New Uploads · 7d" value={dashboard.recent_uploads} />
            <Kpi label="BYOD · 7d" value={dashboard.byod_leads_week} accent="emerald" />
          </div>
        )}

        <div className="mb-4">
          <SoldMarqueeToggle />
        </div>

        {/* Filter card */}
        <form className="bg-white rounded-lg border border-gray-200 shadow-sm mb-4 overflow-hidden" onSubmit={onSubmit} data-testid="admin-crm-filter-form">
          <div className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
              <div className="md:col-span-6">
                <label className={labelCls}>Search</label>
                <input value={filters.q} onChange={(e) => update("q", e.target.value)}
                  className={inputCls} placeholder="Name, phone, email, VIN, stock #…"
                  data-testid="admin-crm-q" />
              </div>
              <div className="md:col-span-2">
                <label className={labelCls}>Status</label>
                <select value={filters.status} onChange={(e) => update("status", e.target.value)} className={inputCls} data-testid="admin-crm-status">
                  {STATUSES.map(s => <option key={s} value={s}>{s || "Any"}</option>)}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className={labelCls}>Temp</label>
                <select value={filters.temperature} onChange={(e) => update("temperature", e.target.value)} className={inputCls}>
                  {TEMPERATURES.map(t => <option key={t} value={t}>{t || "Any"}</option>)}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className={labelCls}>Date</label>
                <select value={filters.date_range} onChange={(e) => update("date_range", e.target.value)} className={inputCls}>
                  {DATE_RANGES.map(d => <option key={d.v} value={d.v}>{d.label}</option>)}
                </select>
              </div>
            </div>

            {advancedOpen && (
              <div className="grid grid-cols-1 md:grid-cols-12 gap-3 mt-3 pt-3 border-t border-gray-100">
                <div className="md:col-span-3">
                  <label className={labelCls}>Missing</label>
                  <select value={filters.missing} onChange={(e) => update("missing", e.target.value)} className={inputCls} data-testid="admin-crm-missing">
                    {MISSING.map(m => <option key={m.v} value={m.v}>{m.label}</option>)}
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className={labelCls}>Has Trade</label>
                  <select value={filters.has_trade} onChange={(e) => update("has_trade", e.target.value)} className={inputCls}>
                    <option value="">Any</option><option value="yes">Yes</option><option value="no">No</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className={labelCls}>Make</label>
                  <input value={filters.vehicle_make} onChange={(e) => update("vehicle_make", e.target.value)} className={inputCls} placeholder="Chevy" />
                </div>
                <div className="md:col-span-2">
                  <label className={labelCls}>Model</label>
                  <input value={filters.vehicle_model} onChange={(e) => update("vehicle_model", e.target.value)} className={inputCls} placeholder="Silverado" />
                </div>
                <div className="md:col-span-2">
                  <label className={labelCls}>VIN</label>
                  <input value={filters.vin} onChange={(e) => update("vin", e.target.value)} className={`${inputCls} font-mono uppercase`} />
                </div>
                <div className="md:col-span-1">
                  <label className={labelCls}>Stock</label>
                  <input value={filters.stock} onChange={(e) => update("stock", e.target.value)} className={`${inputCls} font-mono`} />
                </div>
              </div>
            )}
          </div>
          <div className="bg-slate-50 px-4 py-2.5 border-t border-gray-200 flex flex-wrap items-center justify-between gap-2">
            <button type="button" onClick={() => setAdvancedOpen(v => !v)}
              className="text-xs font-semibold text-blue-700 hover:text-blue-900" data-testid="admin-crm-toggle-advanced">
              {advancedOpen ? "− Hide filters" : "+ More filters (missing fields, vehicle, VIN, stock)"}
            </button>
            <div className="flex gap-2">
              <button type="button" className="text-xs font-semibold text-gray-600 hover:text-[#061426] px-3 py-1.5 rounded" onClick={clearFilters} data-testid="admin-crm-clear">Clear</button>
              <button type="submit" className="bg-[#061426] hover:bg-[#0a1f3a] text-white text-xs font-bold px-4 py-1.5 rounded" data-testid="admin-crm-submit">{loading ? "Searching…" : "Search"}</button>
            </div>
          </div>
        </form>

        <div className="text-xs text-gray-500 mb-2 flex items-center justify-between">
          <span>Showing <b>{items.length}</b> of <b>{count}</b> matching customer{count === 1 ? "" : "s"}.</span>
          {filters.q && <span className="text-blue-700 font-semibold">Filtered by: "{filters.q}"</span>}
        </div>

        {/* Results — desktop table */}
        <div className="hidden md:block bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
          <table className="w-full text-sm" data-testid="admin-crm-results">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-gray-500">
              <tr>
                <th className="px-3 py-2 text-left">Customer</th>
                <th className="px-3 py-2 text-left">Contact</th>
                <th className="px-3 py-2 text-left">Vehicle Interest</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-left">Temp</th>
                <th className="px-3 py-2 text-left">Source</th>
                <th className="px-3 py-2 text-left">Last Touch</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {items.map(it => (
                <tr key={it.id} className="border-t border-gray-100 hover:bg-blue-50/40" data-testid={`admin-crm-row-${it.id}`}>
                  <td className="px-3 py-2 font-semibold text-[#061426]">{it.name || "—"}</td>
                  <td className="px-3 py-2 text-xs text-gray-600">{it.phone || ""}<br />{it.email || ""}</td>
                  <td className="px-3 py-2 text-sm">{it.vehicle_title || it.vehicle_interest || "—"}</td>
                  <td className="px-3 py-2"><StatusPill v={it.status} /></td>
                  <td className="px-3 py-2"><TempPill v={it.temperature} /></td>
                  <td className="px-3 py-2 text-xs text-gray-500">{it.lead_source || ""}</td>
                  <td className="px-3 py-2 text-xs text-gray-500">{(it.last_touch_at || it.created_at || "").slice(0, 16).replace("T", " ")}</td>
                  <td className="px-3 py-2 text-right">
                    <Link to={`/admin/crm/customer/${it.id}`} className="bg-[#006DFF] hover:bg-blue-700 text-white text-xs font-bold px-3 py-1.5 rounded" data-testid={`admin-crm-open-${it.id}`}>Open →</Link>
                  </td>
                </tr>
              ))}
              {!loading && items.length === 0 && (
                <tr><td colSpan={8} className="p-8 text-center text-gray-400">No matching customers.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Results — mobile stacked cards */}
        <div className="md:hidden space-y-2" data-testid="admin-crm-results-mobile">
          {items.map(it => (
            <Link key={it.id} to={`/admin/crm/customer/${it.id}`}
              className="block bg-white rounded-lg border border-gray-200 p-3 shadow-sm hover:border-blue-400"
              data-testid={`admin-crm-card-${it.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="font-bold text-[#061426] truncate">{it.name || "—"}</div>
                  <div className="text-xs text-gray-600 truncate">{it.phone || ""}{it.email ? ` · ${it.email}` : ""}</div>
                  <div className="text-xs text-gray-500 mt-1 truncate">{it.vehicle_title || it.vehicle_interest || ""}</div>
                </div>
                <div className="flex flex-col items-end gap-1 shrink-0">
                  <StatusPill v={it.status} />
                  <TempPill v={it.temperature} />
                </div>
              </div>
              <div className="text-[10px] text-gray-400 mt-2 flex justify-between">
                <span>{it.lead_source || "—"}</span>
                <span>{(it.last_touch_at || it.created_at || "").slice(0, 10)}</span>
              </div>
            </Link>
          ))}
          {!loading && items.length === 0 && (
            <div className="bg-white rounded-lg border border-gray-200 p-8 text-center text-gray-400">No matching customers.</div>
          )}
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value, accent, hint }) {
  const stripe = accent === "red" ? "bg-red-500" :
                 accent === "amber" ? "bg-amber-500" :
                 accent === "emerald" ? "bg-emerald-500" :
                 accent === "blue" ? "bg-blue-500" :
                 "bg-slate-300";
  return (
    <div className="relative rounded-lg border border-gray-200 bg-white p-3 shadow-sm hover:shadow-md transition-shadow overflow-hidden">
      <div className={`absolute top-0 left-0 bottom-0 w-1 ${stripe}`}></div>
      <div className="pl-2">
        <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">{label}</div>
        <div className="text-xl font-extrabold text-[#061426] mt-0.5">{value ?? 0}</div>
        {hint && <div className="text-[10px] text-gray-400 mt-0.5">{hint}</div>}
      </div>
    </div>
  );
}

function StatusPill({ v }) {
  if (!v) return <span className="text-xs text-gray-400">—</span>;
  const c = v === "sold" ? "bg-emerald-100 text-emerald-700" :
            v === "lost" || v === "bad" ? "bg-red-100 text-red-700" :
            v === "new" ? "bg-blue-100 text-blue-700" :
            v === "appointment" ? "bg-purple-100 text-purple-700" :
            "bg-amber-100 text-amber-700";
  return <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wider ${c}`}>{v}</span>;
}

function TempPill({ v }) {
  if (!v) return null;
  const c = v === "Hot" ? "bg-red-100 text-red-700" :
            v === "Warm" ? "bg-amber-100 text-amber-700" :
            "bg-sky-100 text-sky-700";
  return <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${c}`}>{v}</span>;
}
